import { Component, input, output, signal, effect, forwardRef, ChangeDetectionStrategy, ViewChild, ElementRef, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface ToggleChangeEvent {
  checked: boolean;
  value: boolean;
  source: JeptoSlideToggleComponent;
}

@Component({
  selector: 'jepto-slide-toggle',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JeptoSlideToggleComponent),
      multi: true
    }
  ],
  templateUrl: './jepto-slide-toggle.component.html',
  styleUrl: './jepto-slide-toggle.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JeptoSlideToggleComponent implements ControlValueAccessor {
  /**
   * When bound (true/false), keeps the toggle in sync with the parent.
   * When omitted, the value is driven only by {@link ControlValueAccessor} (e.g. `FormControl`),
   * so we must not default to `false` or an effect would overwrite `writeValue`.
   */
  checked = input<boolean | undefined>(undefined);
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  ariaLabel = input<string>('Toggle');
  ariaLabelledby = input<string>('');
  ariaDescribedby = input<string>('');
  tabIndex = input<number>(0);
  name = input<string>('');
  value = input<any>(null);
  testId = input<string>('jepto-slide-toggle');
  /** Hover tooltip on the toggle track (works when disabled; label excluded for placement). */
  tooltip = input<string | null | undefined>(undefined);
  tooltipPosition = input<'above' | 'below' | 'left' | 'right' | 'before' | 'after'>('above');

  /** Trimmed tooltip text; null when unset or whitespace-only. */
  readonly resolvedTooltip = computed(() => {
    const value = this.tooltip();
    if (value == null) {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  });

  // Output events
  change = output<ToggleChangeEvent>();
  toggleChange = output<ToggleChangeEvent>();

  // Internal state
  private _checked = signal<boolean>(false);
  private _disabled = signal<boolean>(false);
  private _id = signal<string>(`jepto-slide-toggle-${Math.random().toString(36).substring(2, 11)}`);

  // ViewChild for input element
  @ViewChild('inputElement') private inputElement?: ElementRef<HTMLInputElement>;

  // ControlValueAccessor callbacks
  private _onChange: (value: boolean) => void = () => {};
  private _onTouched: () => void = () => {};

  constructor() {
    // Sync [checked] with internal state when the input is explicitly set (not CVA-only usage)
    effect(() => {
      const v = this.checked();
      if (v !== undefined) {
        this._checked.set(v);
      }
    });

    // Sync external disabled input with internal state
    effect(() => {
      this._disabled.set(this.disabled());
    });
  }

  // Getters
  get isChecked(): boolean {
    return this._checked();
  }

  get isDisabled(): boolean {
    return this._disabled();
  }

  get toggleId(): string {
    return this._id();
  }

  get cssClasses(): string[] {
    const classes = ['jepto-slide-toggle'];

    if (this.isChecked) {
      classes.push('jepto-slide-toggle--checked');
    }

    if (this.isDisabled) {
      classes.push('jepto-slide-toggle--disabled');
    }

    return classes;
  }

  // Event handlers
  onToggleClick(event: Event): void {
    event.stopPropagation();
    if (this.isDisabled) {
      event.preventDefault();
      return;
    }

    this.toggle();
  }

  onInputChange(event: Event): void {
    if (this.isDisabled) {
      return;
    }

    const target = event.target as HTMLInputElement;
    this.updateCheckedState(target.checked);
  }

  onBlur(): void {
    this._onTouched();
  }

  // Public methods
  toggle(): void {
    if (this.isDisabled) {
      return;
    }

    this.updateCheckedState(!this.isChecked);
  }

  focus(): void {
    this.inputElement?.nativeElement?.focus();
  }

  private updateCheckedState(checked: boolean): void {
    if (this._checked() === checked) {
      return;
    }

    this._checked.set(checked);
    this._onChange(checked);

    const changeEvent: ToggleChangeEvent = {
      checked,
      value: checked,
      source: this
    };

    this.change.emit(changeEvent);
    this.toggleChange.emit(changeEvent);
  }

  // ControlValueAccessor implementation
  writeValue(value: boolean): void {
    this._checked.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled.set(isDisabled);
  }
}
